import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { HOTEL, IMAGES, inr } from "@/constants/hotelData";
import { scrollToSection } from "@/components/Nav";

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
};

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.16 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.24]);

  const [checkIn, setCheckIn] = useState(todayStr());
  const [checkOut, setCheckOut] = useState(addDays(todayStr(), 1));
  const [rooms, setRooms] = useState(1);

  const search = () => {
    if (!checkIn || !checkOut || checkOut <= checkIn) return;
    navigate(`/book?checkin=${checkIn}&checkout=${checkOut}&rooms=${rooms}`);
  };

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Hotel Raj at night"
          className="h-full w-full object-cover"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-ink/40" />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pt-36 pb-16 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mb-6 flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.34em] text-goldlight/90 sm:text-xs"
        >
          <MapPin className="h-3.5 w-3.5 text-gold" />
          Hotel Raj · Dhaka, East Champaran
        </motion.p>

        <h1 className="font-serif text-[clamp(2.9rem,8.5vw,6.5rem)] leading-[1.02] tracking-tight">
          <span className="block overflow-hidden pb-1">
            <motion.span
              variants={lineAnim}
              custom={0}
              initial="hidden"
              animate="show"
              className="block text-cream"
            >
              Royal Stays in the
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              variants={lineAnim}
              custom={1}
              initial="hidden"
              animate="show"
              className="gold-gradient-text block italic"
            >
              Heart of Champaran
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-6 max-w-xl text-base font-light leading-relaxed text-stone-300 sm:text-lg"
        >
          Twenty beautifully kept rooms on Ghorasahan Road, moments from the
          Overbridge at Dhaka — booked online in under two minutes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-5 flex flex-wrap items-center gap-3"
        >
          <span className="shimmer-border rounded-full px-5 py-2 font-mono text-sm text-goldlight" data-testid="hero-price-chip">
            {inr(HOTEL.ratePerNight)} / night
          </span>
          <span className="rounded-full border border-cream/20 px-5 py-2 text-xs uppercase tracking-[0.2em] text-cream/80">
            {HOTEL.totalRooms} Rooms
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25 }}
          className="glass mt-10 flex w-full max-w-3xl flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-end"
          data-testid="hero-search-widget"
        >
          <div className="flex-1">
            <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Check In</label>
            <input
              type="date"
              data-testid="hero-checkin-input"
              value={checkIn}
              min={todayStr()}
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (e.target.value && checkOut <= e.target.value) setCheckOut(addDays(e.target.value, 1));
              }}
              className="w-full rounded-lg border border-cream/15 bg-ink/40 px-3 py-2.5 font-mono text-sm text-cream outline-none transition-colors focus:border-gold/60"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Check Out</label>
            <input
              type="date"
              data-testid="hero-checkout-input"
              value={checkOut}
              min={addDays(checkIn, 1)}
              onChange={(e) => setCheckOut(e.target.value)}
              className="w-full rounded-lg border border-cream/15 bg-ink/40 px-3 py-2.5 font-mono text-sm text-cream outline-none transition-colors focus:border-gold/60"
            />
          </div>
          <div className="sm:w-28">
            <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Rooms</label>
            <select
              data-testid="hero-rooms-select"
              value={rooms}
              onChange={(e) => setRooms(Number(e.target.value))}
              className="w-full rounded-lg border border-cream/15 bg-ink/40 px-3 py-2.5 text-sm text-cream outline-none transition-colors focus:border-gold/60"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n} className="bg-ink">{n}</option>
              ))}
            </select>
          </div>
          <button
            data-testid="hero-check-availability-button"
            onClick={search}
            className="rounded-full bg-gold px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:bg-goldlight hover:shadow-[0_0_28px_rgba(212,175,55,0.4)]"
          >
            Check Availability
          </button>
        </motion.div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={() => scrollToSection("#story")}
        className="absolute bottom-5 left-1/2 z-10 -translate-x-1/2 text-gold/70 hover:text-gold"
        aria-label="Scroll down"
        data-testid="hero-scroll-down"
      >
        <ChevronDown className="h-7 w-7 animate-bounce" />
      </motion.button>
    </section>
  );
}
